import { FC, useState } from "react";
import styles from "./CatalogCard.module.scss";
import Image from "next/image";
import { staticResource } from "@/lib/utils/resources";
import { useTranslation } from "next-i18next";
import { toMoney } from "@/lib/utils/toMoney";
import { H3, P2, P3, P4 } from "@/lib/ui/components/Typography";
import { useAppSelector } from "@/lib/store/app";
import { CircleButton } from "@/lib/ui/components/Buttons";
import { ArrowIcon, CloseSign } from "@/lib/ui/icons";
import cn from "classnames";
import { getDate } from "@/lib/utils/getDate";
import Link from "next/link";
import { getWordEnding } from "@/lib/utils/getWordEnding";
import { ICatalogObject } from "../../../../types/catalogPage";
import { ITooltip } from "../../ITooltip/ITooltip";

interface IPropertyCard {
  data: ICatalogObject;
  theme: "list" | "map";
  onClose?: () => void;
  path: string;
  onTouchStart?: (value: string) => void;
  isTouchedCard?: string | null;
  isFirstCard?: boolean;
}

export const CatalogCard: FC<IPropertyCard> = ({
  data,
  theme,
  onClose,
  path,
  onTouchStart,
  isTouchedCard,
  isFirstCard
}) => {
  const [isError, setIsError] = useState(false);
  const {
    t,
    i18n: { language },
  } = useTranslation("index");
  const { symbol: cur, value } = useAppSelector((state) => state.settings.currency);

  const saleObject = data;

  const districtsString = saleObject.districts.map((item) => t(`locations.${item}`)).join();
  const location = `${t(`locations.${saleObject.region}`)}, ${districtsString}`;
  const isApartments = saleObject.objectType !== "SINGLE_VILLA";
  const money = toMoney(saleObject.price?.[value]);
  const incomeText =
    saleObject.rentalPool && !saleObject.guaranteedIncome
      ? t("rentalPool")
      : t("guaranteedIncome");
  const incomeInfo =
    saleObject.rentalPool && !saleObject.guaranteedIncome && saleObject.rentalPoolPercent
      ? `${saleObject.rentalPoolPercent}/${100 - saleObject.rentalPoolPercent}%`
      : `${saleObject.guaranteedIncomePercent} % ${t("prepositions.for")} ${
          saleObject.guaranteedIncomePeriod
        } ${t(
          saleObject?.guaranteedIncomePeriod && saleObject.guaranteedIncomePeriod > 1
            ? "declinable.years"
            : "declinable.year",
        )}`;
  const constructionState = saleObject.constructionState?.constructionDate
    ? `${t("constructionDate")}:  ${getDate(
        saleObject.constructionState?.constructionDate,
      )}`
    : saleObject.constructionState?.isReady
    ? `${t("readyToMoveIn")}`
    : null;

  const { beach, minutes, method } = saleObject.distanceToBeach;
  const beachText = `${t(`locations.${beach}`)}, ${minutes} ${`${getWordEnding(
    minutes,
    [t("declinable.minute"), t("declinable.minutes"), t("declinable.minutesMore")],
    language as "ru" | "en",
  )} ${method === "FOOT" ? t("byFoot") : t("byCar")}`}`;

  return (
    <div
      className={cn(
        styles.card,
        theme === "map" && styles.cardMap
      )}
      onTouchStart={() => onTouchStart && onTouchStart(path)}
    >
      {theme === "map" && (
        <CircleButton
          name="close card"
          as={"button"}
          onClick={onClose}
          content={<CloseSign />}
          theme="mixed"
          className={styles.cardMapClose}
        />
      )}
      <div
        className={cn(
          styles.gallery,
          isTouchedCard === path && styles.gallery_cardActive,
        )}
      >
        <Link href={path}>
          {constructionState && (
            <div className={styles.top}>
              <P2>{constructionState}</P2>
            </div>
          )}
          <Image
            className={styles.image}
            alt=""
            priority={isFirstCard}
            onError={function (e) {
              if (e.type === "error") {
                setIsError(true);
              }
            }}
            sizes="(min-width: 1640px) 528px, (min-width: 1460px) 437px, (min-width: 1020px) calc(28.1vw + 32px), (min-width: 980px) calc(-625vw + 6562px), (min-width: 580px) 45vw, calc(100vw - 32px)"
            src={
              isError
                ? "/images/placeholder_image.webp"
                : staticResource(saleObject.mainImage) || "/images/placeholder_image.webp"
            }
            fill
          />
          <Image
            priority={isFirstCard}
            className={styles.logo}
            src={staticResource(saleObject.logoImage)}
            sizes="(min-width: 1640px) 528px, (min-width: 1460px) 437px, (min-width: 1020px) calc(28.1vw + 32px), (min-width: 980px) calc(-625vw + 6562px), (min-width: 580px) 45vw, calc(100vw - 32px)"
            alt=""
            onError={(e) => {
              e.currentTarget.src = "";
            }}
            fill
          />
        </Link>
      </div>
      <div className={styles.footer}>
        <div className={styles.footer_top}>
          <Link href={path}>
            <H3 as="h2" className={styles.title}>
              {saleObject.name}
            </H3>
          </Link>
          <button
            type="button"
            name="close card"
            onClick={onClose && onClose}
            className={styles.closeButton}
          >
            <CloseSign />
          </button>
        </div>

        <div className={theme === "map" ? styles.cardMapInfo : styles.info}>
          {theme === "map" && <P4 className={styles.location}>{location}</P4>}
          <div className={styles.mainInfo}>
            <P3>{t("location")}:</P3>
            <P2>{location}</P2>
          </div>
          <div className={styles.mainInfo}>
            <P3>{t("beach")}:</P3>
            <P2>{beachText}</P2>
          </div>
          {(saleObject.guaranteedIncome) && (
            <div className={styles.guaranteedBlock}>
              <div className={styles.tooltipWrapper}>
              <P3>{incomeText}:</P3>
              {saleObject.guaranteedIncome && 
                <ITooltip
                  text={t("guaranteedTooltip")}
                  position={"top"}
                  className={styles.tooltipIcon}
                />
              }
              </div>
              
              <P2>{incomeInfo}</P2>
            </div>
          )}
          {(saleObject.rentalPool && !saleObject.guaranteedIncome) && (
            <div className={styles.mainInfo}>
              <P3>{incomeText}:</P3>
              <P2>{incomeInfo}</P2>
            </div>
          )}
        </div>
        <div className={cn(theme === "map" && styles.cardMapBottom, styles.bottom)}>
          <H3 as="h2" className={styles.price}>
            {isApartments && t("prepositions.from")} {cur}
            {money}
          </H3>
          <CircleButton
            name="go to complex"
            as={Link}
            href={path}
            content={<ArrowIcon isRight />}
            theme={"mixed"}
            className={styles.cardLink}
          />
        </div>
      </div>
    </div>
  );
};

export default CatalogCard;
